<template>
    <b-card
        no-body
    >
        <div class="card-header">
            <div
                class="card-group-title"
            >
                <h4>{{ $t("org.billing.Monthly Cost") }}</h4>
            </div>
        </div>
        <div class="mx-2 mb-2"
        >
            <BarChart
                :data=chartData
                :options=chartOptions
                :height="220"
            >
            </BarChart>

        </div>
    </b-card>
</template>

<script>
import { getCurrentInstance, computed, toRefs, onUnmounted } from '@vue/composition-api'
import useCommonData from '@/composables/useCommonData'
import CreateVueChart from '@/components/CreateVueChart.js'
import { Chart } from 'chart.js'
import ChartJSPluginDatalabels from 'chartjs-plugin-datalabels'
export default {
    components: {
        BarChart: CreateVueChart('Bar')
    },
    props: {
        data: {
            type: Array,
            default: function () {
                return []
            }
        },
        themeColors: {
            type: Object,
            default: function () {
                return {}
            }
        },
        theme: {
            type: String
        },
        month: {
            type: String
        },
        status: {
            type: [String, Number],
            default: ''
        }
    },
    setup (props, context) {
        const vm = getCurrentInstance().proxy
        const { month, themeColors, data } = toRefs(props)
        const yKeys = ['cost']
        const xKey = 'month'
        const { __toKFormat } = useCommonData()
        const noData = computed(() => !data.value.length)
        const maxCost = computed(() => Math.max.apply(null, data.value.map(item => item.cost)))

        const chartData = computed(() => {
            return {
                labels: data.value.map(item => item.month),
                datasets: [{
                    data: data.value.map(item => item.cost),
                    barPercentage: 0.75,
                    backgroundColor: backgroundColorArr.value,
                    hoverBackgroundColor: transparentize(themeColors.value['primary'], 1)
                }]
            }
        })
        const chartOptions = computed(() => {
            return {
                layout: {
                    padding: {
                        top: 20,
                        bottom: 0,
                        left: 10,
                        right: 20
                    }
                },
                animation: false,
                responsive: true,
                maintainAspectRatio: false,
                responsiveAnimationDuration: 500,
                legend: {
                    display: false
                },
                tooltips: {
                    callbacks: {
                        title: tooltipItem => tooltipItem[0].label,
                        label: tooltipItem => ` ${vm.$_getDollar(tooltipItem.value)}`
                    }
                },
                plugins: {
                    noDataBackground: {
                        display: true,
                        imageSrc: require('@/assets/images/empty.png'),
                        text: vm.$t('org.dashboard.No data')
                    },
                    datalabels: {
                        anchor: 'end',
                        align: 'end',
                        display: context => context.chart.config.data.datasets[0].data[context.dataIndex] === maxCost.value,
                        formatter: context => vm.$_getDollar(context)
                    }
                },
                scales: {
                    xAxes: [
                        {
                            display: true,
                            gridLines: {
                                display: false
                            },
                            scaleLabel: {
                                display: false
                            },
                            ticks: {
                                maxTicksLimit: 11,
                                maxRotation: 0
                            }
                        }
                    ],
                    yAxes: [
                        {
                            display: true,
                            gridLines: {
                                display: true,
                                zeroLineColor: 'rgba(200, 200, 200, 0.65)',
                                color: 'rgba(200, 200, 200, 0.65)'
                            },
                            ticks: {
                                callback: getYData,
                                autoSkip: false,
                                maxTicksLimit: 5,
                                suggestedMax: maxCost.value * 1.1
                            }
                        }
                    ]
                },
                onClick: handleClickBar,
                onHover: (event, chartElement) => { event.target.style.cursor = chartElement.length ? 'pointer' : 'default' }
            }
        })
        const backgroundColorArr = computed(() => {
            return data.value.map(item => month.value === item.month
                ? transparentize(themeColors.value['primary'], 1)
                : transparentize(themeColors.value['primary'], 0.6))
        })

        const getYData = (label) => __toKFormat(label)

        const handleClickBar = (event, chartElement) => {
            if (chartElement.length) {
                context.emit('change:month', { value: data.value[chartElement[0]._index].month })
            }
        }
        const transparentize = (value, opacity) => {
            const r = parseInt(value.slice(1, 3), 16)
            const g = parseInt(value.slice(3, 5), 16)
            const b = parseInt(value.slice(5, 7), 16)
            return `rgba(${r}, ${g}, ${b}, ${opacity})`
        }

        Chart.plugins.register(ChartJSPluginDatalabels)
        onUnmounted(() => {
            Chart.plugins.unregister(ChartJSPluginDatalabels)
        })

        return {
            __toKFormat,
            yKeys,
            xKey,
            chartData,
            chartOptions,
            noData,
            maxCost
        }
    }
}
</script>

<style>

</style>
